/** Restech-primary choice for the AI import launch form.
 *
 *  Shown when the tenant has a booking integration that can enumerate its
 *  products (`supports_product_list`) and the run can produce tours. The
 *  choice is REQUIRED — importing a web catalog next to an unacknowledged
 *  booking-system catalog silently mints duplicate tours, so the operator
 *  must explicitly pick a source of truth.
 */
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { AiImportLaunchContext, AiImportRestechAccount } from '@/api/aiImport';
import { FieldHelp } from '@/components/FieldHelp';

import { AI_IMPORT_HELP } from './fieldHelp';

export type RestechChoice = 'restech' | 'web' | null;

export interface RestechSourceValue {
  choice: RestechChoice;
  integration: string;
  /** Selected account slugs (null = the default account). */
  accounts: (string | null)[];
}

interface Props {
  restech: NonNullable<AiImportLaunchContext['restech']>;
  value: RestechSourceValue;
  onChange: (value: RestechSourceValue) => void;
}

/** Accounts able to list products, grouped by integration. */
export function listableAccounts(
  restech: AiImportLaunchContext['restech'],
): Map<string, AiImportRestechAccount[]> {
  const grouped = new Map<string, AiImportRestechAccount[]>();
  for (const account of restech?.accounts ?? []) {
    if (!account.supports_product_list) continue;
    const list = grouped.get(account.integration) ?? [];
    list.push(account);
    grouped.set(account.integration, list);
  }
  return grouped;
}

const accountKey = (slug: string | null) => slug ?? ' default';

export function RestechSourcePanel({ restech, value, onChange }: Props) {
  const grouped = listableAccounts(restech);
  const integrations = [...grouped.keys()];
  if (integrations.length === 0) return null;

  const integration = value.integration || integrations[0]!;
  const accounts = grouped.get(integration) ?? [];

  const setChoice = (choice: RestechChoice) =>
    onChange({
      choice,
      integration,
      // default: every account of the integration participates
      accounts: value.accounts.length > 0 ? value.accounts : accounts.map((a) => a.account),
    });

  const toggleAccount = (slug: string | null) => {
    const selected = new Set(value.accounts.map(accountKey));
    const key = accountKey(slug);
    if (selected.has(key)) selected.delete(key);
    else selected.add(key);
    onChange({
      ...value,
      accounts: accounts
        .map((a) => a.account)
        .filter((a) => selected.has(accountKey(a))),
    });
  };

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <FormControl required>
        <FormLabel sx={{ typography: 'subtitle2' }}>
          This tenant has a booking system — which catalog is the source of truth?{' '}
          <FieldHelp help={AI_IMPORT_HELP.sourceOfTruth} />
        </FormLabel>
        <RadioGroup
          value={value.choice ?? ''}
          onChange={(_, v) => setChoice(v as RestechChoice)}
        >
          <FormControlLabel
            value="restech"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">Booking system (recommended)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Web drafts are matched against the product list; matched records
                  bind to the product, unmatched web drafts are unchecked by default.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="web"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">Website only</Typography>
                <Typography variant="caption" color="text.secondary">
                  Import web records as-is — may duplicate products the booking
                  system already tracks.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {value.choice === 'restech' && (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {integrations.length > 1 && (
            <TextField
              select
              size="small"
              label="Integration"
              value={integration}
              onChange={(e) =>
                onChange({
                  choice: value.choice,
                  integration: e.target.value,
                  accounts: (grouped.get(e.target.value) ?? []).map((a) => a.account),
                })
              }
              sx={{ maxWidth: 260 }}
            >
              {integrations.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
          )}
          {accounts.length > 1 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Accounts to match against
              </Typography>
              {accounts.map((account) => (
                <FormControlLabel
                  key={accountKey(account.account)}
                  control={
                    <Checkbox
                      size="small"
                      checked={value.accounts.some(
                        (slug) => accountKey(slug) === accountKey(account.account),
                      )}
                      onChange={() => toggleAccount(account.account)}
                    />
                  }
                  label={account.display_name}
                />
              ))}
            </Box>
          )}
          {value.accounts.length === 0 && (
            <Alert severity="warning">Select at least one account.</Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
